import { call, put, takeLatest, type SagaReturnType } from "redux-saga/effects";

import { toastShowErrorAction } from "@ds/modules/notifications/redux/actions";
import { projectsService } from "@ds/modules/settings/projects/utils/api";
import { usersActions } from "@ds/modules/settings/users/redux/slice";

import { UnexpectedError } from "@ds/utils/errors";
import { logger } from "@ds/utils/logger";
import { takeLatestOrEvery } from "@ds/utils/saga-helpers";
import { isErrorLike } from "@ds/utils/type-guards/error-guards";

import { featuresActions } from "./slice";

function* fetchFeatures({ meta }: ReturnType<typeof featuresActions.fetchFeatures>) {
  try {
    const result: SagaReturnType<typeof projectsService.getProject> = yield call(
      [projectsService, projectsService.getProject],
      meta.projectId,
    );

    yield put(featuresActions.fetchFeaturesSucceeded((result as Project & { features: Feature[] }).features, meta));
  } catch (err) {
    const errorTitle = "Fetch features";
    if (isErrorLike(err)) {
      yield put(featuresActions.fetchFeaturesFailed(meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* fetchCurrentProjectFeaturesOnUserFetch({ payload }: ReturnType<typeof usersActions.fetchUserSucceeded>) {
  const apiUser = payload as ApiUser;
  if (apiUser.current_project_id) {
    yield put(featuresActions.fetchFeatures(apiUser.current_project_id));
  }
}

function* fetchCurrentProjectFeaturesOnProjectIdUpdate({
  payload,
}: ReturnType<typeof usersActions.updateUserCurrentProjectIdSucceeded>) {
  if (payload.current_project_id) {
    yield put(featuresActions.fetchFeatures(payload.current_project_id));
  }
}

export function* watchFeatures() {
  yield takeLatestOrEvery(featuresActions.fetchFeatures, fetchFeatures);
  yield takeLatest(usersActions.fetchUserSucceeded, fetchCurrentProjectFeaturesOnUserFetch);
  yield takeLatest(usersActions.updateUserCurrentProjectIdSucceeded, fetchCurrentProjectFeaturesOnProjectIdUpdate);
}
